import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Grid, Card, CardContent, Chip, Button, CircularProgress, Alert } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ApiService from '../../services/api';
import { isBoostActive, boostCardSx } from '../../utils/boost';

export default function EmployerDirectory() {
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');

    ApiService.getEmployers({ page: 1, limit: 12 })
      .then((res) => {
        if (!mounted) return;
        const list = res.users || res.data?.users || [];
        // Boosted-first, then featured, then newest
        const sorted = [...list].sort((a, b) => {
          const aBoost = isBoostActive(a?.boosted, a?.boostExpiresAt) ? 1 : 0;
          const bBoost = isBoostActive(b?.boosted, b?.boostExpiresAt) ? 1 : 0;
          if (bBoost !== aBoost) return bBoost - aBoost;
          const aFeat = a?.featured ? 1 : 0;
          const bFeat = b?.featured ? 1 : 0;
          if (bFeat !== aFeat) return bFeat - aFeat;
          const aDate = new Date(a.createdAt || 0).getTime();
          const bDate = new Date(b.createdAt || 0).getTime();
          return bDate - aDate;
        });
        setEmployers(sorted);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err.message || 'Failed to load employers');
      })
      .finally(() => mounted && setLoading(false));

    return () => { mounted = false; };
  }, []);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h3" component="h1" gutterBottom>
            Employers
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Browse employers and view their company info
          </Typography>
        </Box>
        <Button variant="contained" component={RouterLink} to="/jobs">
          Browse Jobs
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

  {!loading && !error && (
    <Grid container spacing={3} alignItems="stretch">
      {employers.map((employer) => {
        const companyName = employer.employerProfile?.companyName || `${employer.firstName} ${employer.lastName}`;
        const industry = employer.employerProfile?.industry;
        const city = employer.employerProfile?.companyAddress?.city || employer.location?.city;
        const country = employer.employerProfile?.companyAddress?.country || employer.location?.country;
        const location = [city, country].filter(Boolean).join(', ');
        const boostActive = isBoostActive(employer?.boosted, employer?.boostExpiresAt);

        return (
          <Grid item xs={12} sm={6} md={4} key={employer._id} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', ...(boostActive ? boostCardSx : {}) }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                {(() => {
                  const rawLogo = employer.employerProfile?.companyLogo || employer.companyLogo || employer.logo || '';
                  const logoSrc = (typeof rawLogo === 'string' && rawLogo.trim()) ? rawLogo : '/logo192.png';
                  return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 120,
                            mb: 2,
                            p: 1,
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Box
                            component="img"
                            src={logoSrc}
                            alt={`${companyName} logo`}
                            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          />
                        </Box>
                  );
                })()}
                <Box sx={{ mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{companyName}</Typography>
                  <Typography variant="body2" color="text.secondary">{location}</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {boostActive && (<Chip label="Boosted" color="secondary" size="small" sx={{ mr: 1 }} />)}
                  </Box>
                </Box>
                {industry && <Chip label={industry} size="small" sx={{ mr: 1 }} />}
                <Box sx={{ mt: 'auto' }}>
                  <Button
                    variant="outlined"
                    component={RouterLink}
                    to={`/jobs?employerId=${employer._id}`}
                  >
                    View Open Jobs
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  )}
    </Container>
  );
}